/**
 * Dialog de configuração do node "Atribuir Responsável da Tarefa" (task_assign)
 *
 * RELEASE: 1.0.0 - 2026-05-31 - Versão inicial
 *
 * Permite escolher a tarefa-alvo (gatilho ou variável) e o modo de atribuição
 * do responsável: usuário fixo, vendedor do negócio vinculado, variável, ou
 * remoção do responsável atual.
 */

import { useEffect, useRef, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Separator } from '@/components/ui/separator';
import { UserCog, ListTodo, User, HelpCircle } from 'lucide-react';
import { FlowNode, TaskAssignConfig } from '@/types/flows';
import { useAuth } from '@/hooks/useAuth';
import { showToast } from '@/lib/toast';
import { NodeHelpDialog } from './NodeHelpDialog';
import { NodeIdField, NodeIdFieldRef } from '../NodeIdField';
import { VariablePickerCombobox } from '../VariablePickerCombobox';
import { SystemUserSelector } from '@/components/crm/SystemUserSelector';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  node: FlowNode;
  onSave: (nodeId: string, config: TaskAssignConfig) => void;
}

export function ConfigureTaskAssignDialog({ open, onOpenChange, node, onSave }: Props) {
  const { currentTenant } = useAuth();
  const tenantId = currentTenant?.id || '';
  const nicknameRef = useRef<NodeIdFieldRef>(null);

  const existing = node.data.config as TaskAssignConfig | undefined;

  // Estado: origem da tarefa
  const [taskSource, setTaskSource] = useState<TaskAssignConfig['task_source']>(
    existing?.task_source || 'from_trigger',
  );
  const [taskVariable, setTaskVariable] = useState(existing?.task_variable || '');
  const [taskVariableId, setTaskVariableId] = useState(existing?.task_variable_id || '');

  // Estado: responsável
  const [assigneeMode, setAssigneeMode] = useState<TaskAssignConfig['assignee_mode']>(
    existing?.assignee_mode || 'manual',
  );
  const [assigneeId, setAssigneeId] = useState(existing?.assignee_id || '');
  const [assigneeName, setAssigneeName] = useState(existing?.assignee_name || '');
  const [assigneeVariable, setAssigneeVariable] = useState(existing?.assignee_variable || '');
  const [assigneeVariableId, setAssigneeVariableId] = useState(existing?.assignee_variable_id || '');

  const [showHelp, setShowHelp] = useState(false);

  // Reset ao reabrir o dialog
  useEffect(() => {
    if (!open) return;
    const cfg = node.data.config as TaskAssignConfig | undefined;
    setTaskSource(cfg?.task_source || 'from_trigger');
    setTaskVariable(cfg?.task_variable || '');
    setTaskVariableId(cfg?.task_variable_id || '');
    setAssigneeMode(cfg?.assignee_mode || 'manual');
    setAssigneeId(cfg?.assignee_id || '');
    setAssigneeName(cfg?.assignee_name || '');
    setAssigneeVariable(cfg?.assignee_variable || '');
    setAssigneeVariableId(cfg?.assignee_variable_id || '');
  }, [open, node.id]);

  const isFormValid = (): boolean => {
    if (taskSource === 'from_variable' && !taskVariable.trim()) return false;
    if (assigneeMode === 'manual' && !assigneeId) return false;
    if (assigneeMode === 'from_variable' && !assigneeVariable.trim()) return false;
    return true;
  };

  const handleSave = () => {
    if (!isFormValid()) {
      showToast.error('Preencha todos os campos obrigatórios');
      return;
    }
    const config: TaskAssignConfig = {
      nickname: nicknameRef.current?.getNickname() || undefined,
      task_source: taskSource,
      task_variable: taskSource === 'from_variable' ? taskVariable.trim() : undefined,
      task_variable_id: taskSource === 'from_variable' ? taskVariableId || undefined : undefined,
      assignee_mode: assigneeMode,
      assignee_id: assigneeMode === 'manual' ? assigneeId : undefined,
      assignee_name: assigneeMode === 'manual' ? assigneeName || undefined : undefined,
      assignee_variable: assigneeMode === 'from_variable' ? assigneeVariable.trim() : undefined,
      assignee_variable_id: assigneeMode === 'from_variable' ? assigneeVariableId || undefined : undefined,
    };
    onSave(node.id, config);
    onOpenChange(false);
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-2xl h-[85vh] max-h-[85vh] flex flex-col overflow-hidden min-h-0">
          <DialogHeader className="shrink-0">
            <div className="flex items-center justify-between">
              <DialogTitle className="flex items-center gap-2">
                <UserCog className="h-5 w-5 text-amber-600" />
                Configurar Atribuir Responsável
              </DialogTitle>
              <Button variant="ghost" size="icon" onClick={() => setShowHelp(true)}>
                <HelpCircle className="h-5 w-5" />
              </Button>
            </div>
            <DialogDescription>
              Define ou remove o responsável de uma tarefa existente
            </DialogDescription>
          </DialogHeader>

          <div className="flex-1 min-h-0 overflow-y-auto pr-4">
            <div className="space-y-4 py-4">
              <NodeIdField ref={nicknameRef} nodeId={node?.id} defaultNickname={node?.data.config?.nickname} />

              {/* ===================== TAREFA DE ORIGEM ===================== */}
              <div className="space-y-3">
                <div className="flex items-center gap-2 text-sm font-semibold text-amber-600 dark:text-amber-400">
                  <ListTodo className="h-4 w-4" />
                  Tarefa de origem
                </div>
                <RadioGroup value={taskSource} onValueChange={(v) => setTaskSource(v as any)}>
                  <div className="flex items-start space-x-2">
                    <RadioGroupItem value="from_trigger" id="ts-trigger" className="mt-1" />
                    <div className="space-y-0.5">
                      <Label htmlFor="ts-trigger" className="font-normal cursor-pointer">
                        Tarefa do gatilho
                      </Label>
                      <p className="text-xs text-muted-foreground">
                        Usa o <code>task_id</code> propagado por um gatilho de Tarefa
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-2">
                    <RadioGroupItem value="from_variable" id="ts-var" className="mt-1" />
                    <div className="space-y-0.5">
                      <Label htmlFor="ts-var" className="font-normal cursor-pointer">
                        De uma variável
                      </Label>
                      <p className="text-xs text-muted-foreground">
                        Usa um <code>task_id</code> armazenado em variável (ex.: saída de "Criar Tarefa")
                      </p>
                    </div>
                  </div>
                </RadioGroup>

                {taskSource === 'from_variable' && (
                  <VariablePickerCombobox
                    tenantId={tenantId}
                    value={taskVariableId || taskVariable}
                    onChange={(v) => {
                      if (v) {
                        setTaskVariableId(v.id);
                        setTaskVariable(v.name);
                      } else {
                        setTaskVariableId('');
                        setTaskVariable('');
                      }
                    }}
                    allowCreate={false}
                    allowClear={false}
                    placeholder="Variável com ID da tarefa"
                  />
                )}
              </div>

              <Separator />

              {/* ===================== RESPONSÁVEL ===================== */}
              <div className="space-y-3">
                <div className="flex items-center gap-2 text-sm font-semibold text-amber-600 dark:text-amber-400">
                  <User className="h-4 w-4" />
                  Novo responsável
                </div>
                <RadioGroup value={assigneeMode} onValueChange={(v) => setAssigneeMode(v as any)}>
                  <div className="flex items-start space-x-2">
                    <RadioGroupItem value="manual" id="am-manual" className="mt-1" />
                    <div className="space-y-0.5">
                      <Label htmlFor="am-manual" className="font-normal cursor-pointer">
                        Usuário fixo
                      </Label>
                      <p className="text-xs text-muted-foreground">
                        Sempre atribui ao mesmo usuário escolhido aqui
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-2">
                    <RadioGroupItem value="deal_seller" id="am-deal" className="mt-1" />
                    <div className="space-y-0.5">
                      <Label htmlFor="am-deal" className="font-normal cursor-pointer">
                        Vendedor do negócio
                      </Label>
                      <p className="text-xs text-muted-foreground">
                        Resolve em runtime usando <code>deal_id</code> do contexto ou da tarefa
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-2">
                    <RadioGroupItem value="from_variable" id="am-var" className="mt-1" />
                    <div className="space-y-0.5">
                      <Label htmlFor="am-var" className="font-normal cursor-pointer">
                        De uma variável
                      </Label>
                      <p className="text-xs text-muted-foreground">Usa o <code>user_id</code> contido em variável do fluxo</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-2">
                    <RadioGroupItem value="clear" id="am-clear" className="mt-1" />
                    <div className="space-y-0.5">
                      <Label htmlFor="am-clear" className="font-normal cursor-pointer">
                        Remover responsável
                      </Label>
                      <p className="text-xs text-muted-foreground">
                        Limpa o responsável atual da tarefa (assigned_to = vazio)
                      </p>
                    </div>
                  </div>
                </RadioGroup>

                {assigneeMode === 'manual' && (
                  <SystemUserSelector
                    value={assigneeId || null}
                    onSelect={(userId, user) => {
                      setAssigneeId(userId || '');
                      setAssigneeName(user?.full_name || user?.email || '');
                    }}
                    placeholder="Selecionar usuário..."
                  />
                )}

                {assigneeMode === 'from_variable' && (
                  <VariablePickerCombobox
                    tenantId={tenantId}
                    value={assigneeVariableId || assigneeVariable}
                    onChange={(v) => {
                      if (v) {
                        setAssigneeVariableId(v.id);
                        setAssigneeVariable(v.name);
                      } else {
                        setAssigneeVariableId('');
                        setAssigneeVariable('');
                      }
                    }}
                    allowCreate={false}
                    allowClear={false}
                    placeholder="Variável com user_id"
                  />
                )}
              </div>
            </div>
          </div>

          <DialogFooter className="shrink-0 border-t pt-4">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Cancelar
            </Button>
            <Button onClick={handleSave} disabled={!isFormValid()}>
              Salvar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <NodeHelpDialog open={showHelp} onOpenChange={setShowHelp} nodeType="task_assign" />
    </>
  );
}
